const API_BASE = process.env.REACT_APP_BACKEND_URL;
const GEO_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = `${API_BASE}/api/crm/lead`;

// Country codes list with dial codes
export const countryCodes = [
  { dialCode: 1, country: 'US', label: 'US +1' },
  { dialCode: 1, country: 'CA', label: 'CA +1' },
  { dialCode: 44, country: 'GB', label: 'UK +44' },
  { dialCode: 49, country: 'DE', label: 'DE +49' },
  { dialCode: 33, country: 'FR', label: 'FR +33' },
  { dialCode: 61, country: 'AU', label: 'AU +61' },
  { dialCode: 91, country: 'IN', label: 'IN +91' },
  { dialCode: 86, country: 'CN', label: 'CN +86' },
  { dialCode: 81, country: 'JP', label: 'JP +81' },
  { dialCode: 82, country: 'KR', label: 'KR +82' },
  { dialCode: 55, country: 'BR', label: 'BR +55' },
  { dialCode: 52, country: 'MX', label: 'MX +52' },
  { dialCode: 971, country: 'AE', label: 'AE +971' },
  { dialCode: 966, country: 'SA', label: 'SA +966' },
  { dialCode: 65, country: 'SG', label: 'SG +65' },
  { dialCode: 852, country: 'HK', label: 'HK +852' },
  { dialCode: 31, country: 'NL', label: 'NL +31' },
  { dialCode: 34, country: 'ES', label: 'ES +34' },
  { dialCode: 39, country: 'IT', label: 'IT +39' },
  { dialCode: 41, country: 'CH', label: 'CH +41' },
  { dialCode: 46, country: 'SE', label: 'SE +46' },
  { dialCode: 47, country: 'NO', label: 'NO +47' },
  { dialCode: 45, country: 'DK', label: 'DK +45' },
  { dialCode: 358, country: 'FI', label: 'FI +358' },
  { dialCode: 48, country: 'PL', label: 'PL +48' },
  { dialCode: 43, country: 'AT', label: 'AT +43' },
  { dialCode: 32, country: 'BE', label: 'BE +32' },
  { dialCode: 353, country: 'IE', label: 'IE +353' },
  { dialCode: 351, country: 'PT', label: 'PT +351' },
  { dialCode: 64, country: 'NZ', label: 'NZ +64' },
  { dialCode: 27, country: 'ZA', label: 'ZA +27' },
  { dialCode: 234, country: 'NG', label: 'NG +234' },
  { dialCode: 254, country: 'KE', label: 'KE +254' },
  { dialCode: 20, country: 'EG', label: 'EG +20' },
  { dialCode: 60, country: 'MY', label: 'MY +60' },
  { dialCode: 63, country: 'PH', label: 'PH +63' },
  { dialCode: 66, country: 'TH', label: 'TH +66' },
  { dialCode: 62, country: 'ID', label: 'ID +62' },
  { dialCode: 84, country: 'VN', label: 'VN +84' },
  { dialCode: 90, country: 'TR', label: 'TR +90' },
  { dialCode: 7, country: 'RU', label: 'RU +7' },
  { dialCode: 380, country: 'UA', label: 'UA +380' },
  { dialCode: 972, country: 'IL', label: 'IL +972' },
  { dialCode: 92, country: 'PK', label: 'PK +92' },
  { dialCode: 880, country: 'BD', label: 'BD +880' },
  { dialCode: 94, country: 'LK', label: 'LK +94' },
  { dialCode: 56, country: 'CL', label: 'CL +56' },
  { dialCode: 57, country: 'CO', label: 'CO +57' },
  { dialCode: 54, country: 'AR', label: 'AR +54' },
  { dialCode: 51, country: 'PE', label: 'PE +51' },
];

// Cached geo data
let geoCache = null;

/**
 * Fetch geolocation data for the current user.
 * Called directly from browser to detect user's real IP.
 * Caches the result so it only calls the API once per session.
 */
export async function getGeoLocation() {
  if (geoCache) return geoCache;

  try {
    const res = await fetch(GEO_API, {
      headers: { 'accept': 'text/plain' }
    });
    const data = await res.json();
    if (data.isSuccess) {
      geoCache = data;
      return data;
    }
  } catch (err) {
    // Geo API unavailable — forms still work without it
  }
  return null;
}

/**
 * Find the matching country entry from countryCodes list by country code string.
 */
export function findCountryByCode(code) {
  if (!code) return countryCodes[0];
  const upper = code.toUpperCase();
  return countryCodes.find(c => c.country === upper) || countryCodes[0];
}

/**
 * Submit a lead to the CRM API via backend proxy.
 * Backend always returns HTTP 200 — actual status is in body `statusCode`.
 */
export async function submitLead({ name, email, countryDialCode, country, phoneNumber, description, city, state, ipAddress, geoLocation }) {
  const payload = {
    name,
    email,
    countryDialCode: Number(countryDialCode),
    country: country || '',
    phoneNumber: phoneNumber.replace(/\D/g, ''),
    ipAddress: ipAddress || '',
    description: description || '',
    pageUrl: window.location.href,
    city: city || '',
    state: state || '',
    geoLocation: geoLocation || '',
    packageTitle: '',
    packageAmount: 0
  };

  try {
    const res = await fetch(LEAD_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const data = await res.json();
    const status = data.statusCode || data.status;

    // Success: statusCode 200 with customerId
    if (status === 200 && data.isSuccess && data.data?.customerId) {
      return { success: true, customerId: data.data.customerId };
    }

    // 401: domain not configured
    if (status === 401) {
      return { success: false, error: data.message || 'Unauthorized request' };
    }

    // 400: validation error
    if (status === 400) {
      // Handle both formats: {errorMessage} and {errors: {field: [msg]}}
      if (data.errorMessage) return { success: false, error: data.errorMessage };
      if (data.errors) {
        const firstError = Object.values(data.errors).flat()[0];
        return { success: false, error: firstError || 'Validation error' };
      }
      return { success: false, error: data.title || 'Invalid request' };
    }

    return { success: false, error: data.errorMessage || data.message || 'Something went wrong' };
  } catch (err) {
    return { success: false, error: 'Network error. Please try again.' };
  }
}
